import amplitude, { type AmplitudeClient } from 'amplitude-js'
import { getMobileOperatingSystem } from 'helpers/getMobileOperatingSystem'
import { getOperatingSystemVersion } from 'helpers/getOperatingSystemVersion'
import type {
  IEventLogger,
  IEventLogParams,
  TCommonEventProperties,
} from 'models/events.model'
import { EventLoggerInstanceName } from 'root-constants'

export class Amplitude implements IEventLogger {
  public readonly name: EventLoggerInstanceName
  private readonly amplitude: AmplitudeClient
  private utmTags: Record<string, string>
  private cohort = ''
  private userId = ''
  private readonly isProdEnvironment: boolean
  private userProperties: Record<string, unknown> = {}

  constructor({
    instanceName,
    apiKey,
    userId,
    utmTags,
    options,
    isProdEnvironment,
  }: {
    instanceName: EventLoggerInstanceName
    apiKey: string
    userId: string
    utmTags: Record<string, string>
    options?: Record<string, unknown>
    isProdEnvironment: boolean
  }) {
    this.utmTags = utmTags
    this.name = instanceName
    this.userId = userId
    this.amplitude = amplitude.getInstance(instanceName)
    this.amplitude.init(apiKey, userId || undefined, options)
    this.isProdEnvironment = isProdEnvironment
  }

  log({ event, eventProperty, cb }: IEventLogParams): void {
    const mergedEventProperty: TCommonEventProperties = {
      cohort: this.cohort,
      attribution: this.utmTags,
      operating_system_version: getOperatingSystemVersion(),
      is_prod_env: this.isProdEnvironment,
      ...eventProperty,
    }

    this.amplitude.setUserId(this.userId || null)
    this.amplitude.logEvent(event, mergedEventProperty, cb)
  }

  configure({
    cohort,
    options,
  }: {
    cohort: string
    options?: Record<string, unknown>
  }): void {
    this.cohort = cohort
    this.setUserProperties(options)
  }

  updateUserProperties(customProps: Record<string, any>): void {
    const updatedUserProperties = {
      ...this.userProperties,
      ...customProps,
    }

    this.userProperties = updatedUserProperties
    this.amplitude.setUserProperties(updatedUserProperties)
  }

  private setUserProperties(options?: Record<string, unknown>): void {
    const userProperties = {
      'Device Type': getMobileOperatingSystem(),
      'Operating System Version': getOperatingSystemVersion(),
      env: import.meta.env.CURRENT_ENV,
      attribution: this.utmTags,
      ...options,
    }

    this.userProperties = userProperties
    this.amplitude.setUserProperties(userProperties)
  }
}
